import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Store, HeartHandshake, TrendingUp, ArrowRight } from "lucide-react";
import { useSeo } from "../lib/useSeo";
import { htmlLangOf } from "../i18n";
import { Button } from "../components/ui/button";

const PILLARS = [
  { key: "ambassadors", icon: Users },
  { key: "partners", icon: Store },
  { key: "community", icon: HeartHandshake },
  { key: "growth", icon: TrendingUp },
];
const FIELD_IMG = "https://images.pexels.com/photos/38196135/pexels-photo-38196135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function PublicAbout() {
  const { t, i18n } = useTranslation();

  useSeo({
    title: `${t("pub.aboutTitle")} · VytaliDays`,
    description: t("pub.aboutLead"),
    path: "/about",
    lang: htmlLangOf(i18n.language),
  });

  return (
    <div data-testid="public-about-page">
      <section className="vy-hero px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("pub.eyebrow")}</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            {t("pub.aboutTitle")}
          </h1>
          <p className="mt-5 text-base text-white/75 sm:text-lg">{t("pub.aboutLead")}</p>
          <p className="mt-6 font-display text-lg font-bold text-accent">{t("pub.strategic")}</p>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-extrabold">{t("pub.aboutHow")}</h2>
          <ol className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {t("pub.aboutSteps", { returnObjects: true }).map((s, i) => (
              <li key={s.title} data-testid={`about-step-${i}`}
                className="rounded-2xl border border-border bg-card p-6">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary font-display text-sm font-extrabold text-primary-foreground">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-secondary px-4 py-16 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-extrabold">{t("pub.whatTitle")}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map(({ key, icon: Icon }) => (
              <div key={key} data-testid={`about-pillar-${key}`} className="rounded-2xl border border-border bg-card p-6">
                <Icon className="h-6 w-6 text-primary" />
                <h3 className="mt-4 font-display text-lg font-bold">{t(`pub.pillars.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t(`pub.pillars.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <img src={FIELD_IMG} alt={t("pub.communityTitle")} loading="lazy"
            className="aspect-[4/3] w-full rounded-3xl object-cover" />
          <div>
            <h2 className="font-display text-3xl font-extrabold">{t("pub.communityTitle")}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t("pub.communityText")}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="h-14 rounded-full px-7 text-base font-bold">
                <Link to="/events" data-testid="about-cta-events">
                  {t("pub.ctaEvents")}<ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-14 rounded-full px-7 text-base font-bold">
                <Link to="/partners" data-testid="about-cta-partner">{t("pub.ctaPartner")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
